"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Facebook, Linkedin, Instagram } from "lucide-react";
import { XIcon } from "@/components/icons/x-icon";
import { getSiteConfig } from "@/lib/config";


export function SocialWidget({ bgClassName = "bg-white", iconColorClass = "text-secondary" }: { bgClassName?: string; iconColorClass?: string }) {
    const config = getSiteConfig();
    const { facebook, linkedin, twitter, instagram } = config.social;

    const socialLinks = [
        { href: facebook, icon: Facebook, label: "Facebook" },
        { href: twitter, icon: XIcon, label: "X" },
        { href: instagram, icon: Instagram, label: "Instagram" },
    ].filter((link) => link.href);

    const [isHovered, setIsHovered] = useState(false);

    // Mobile View: Static list of all icons
    const MobileView = () => (
        <div className="flex md:hidden items-center gap-4">
            {socialLinks.map((social, index) => (
                <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${iconColorClass} hover:text-[#7db336] transition-colors`}
                    title={social.label}
                    aria-label={social.label}
                >
                    <social.icon className="h-5 w-5" />
                </a>
            ))}
            {linkedin && (
                <a
                    href={linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="LinkedIn"
                    className={`${iconColorClass} hover:text-[#7db336] transition-colors`}
                >
                    <Linkedin className="h-6 w-6" />
                </a>
            )}
        </div>
    );

    // Desktop View: Hover widget
    const DesktopView = () => (
        <div
            className="hidden md:flex relative items-center pr-[6px]" // padding right for the trigger area
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div className="relative h-8 flex items-center justify-end">
                <AnimatePresence>
                    {isHovered && (
                        <motion.div
                            initial={{ width: 0, opacity: 0 }}
                            animate={{ width: "auto", opacity: 1 }}
                            exit={{ width: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                            className="flex items-center"
                        >
                            <div className="flex items-center gap-3 pr-3 overflow-hidden">
                                {socialLinks.map((social, index) => (
                                    <motion.a
                                        key={index}
                                        href={social.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`${iconColorClass} hover:text-[#7db336] transition-colors flex-shrink-0`}
                                        title={social.label}
                                        aria-label={social.label}
                                        initial={{ opacity: 0, x: 10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: index * 0.05 }}
                                    >
                                        <social.icon className="h-5 w-5" />
                                    </motion.a>
                                ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                <a
                    href={linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="LinkedIn"
                    className={`z-10 pl-1 cursor-pointer ${bgClassName} ${iconColorClass} hover:text-[#7db336] transition-colors`}
                >
                    <Linkedin className="h-6 w-6" />
                </a>
            </div>
        </div>
    );

    return (
        <>
            <MobileView />
            <DesktopView />
        </>
    );
}

